import { useMutation, useQuery, UseQueryOptions, useQueryClient } from 'react-query'
import { QueryKey } from 'config/constants/queryKey'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { getAddress } from 'utils/addressHelpers'
import { ethers } from 'ethers'
import rewarderV2Abi from 'config/abi/rewarderV2.json'
import { getContract } from 'utils/contractHelpers'
import { useWeb3React } from '@web3-react/core'
import { Web3Provider } from '@ethersproject/providers'
import { simpleRpcProvider } from 'utils/providers'
import farms from 'config/constants/farms'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import { ecoFarm } from 'config/constants/ecoBoost'
import { useArgoNftContract } from 'hooks/useContract'
import useToast from 'hooks/useToast'
import { useTranslation } from 'contexts/Localization'
import { useCallback, useMemo } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { RewarderV2 } from '../../../types/ethers-contracts'

const DEFAULT_REWARDER_V2_ADDRESS = '0x0000000000000000000000000000000000000000'

export const getRewarderV2AddressByPid = (pid: number) => {
  const targetFarm = farms.find((farm) => farm.pid === pid)

  if (isNilOrUndef(targetFarm)) return null

  // 1. config in farms, restriction : only support 1 rewarderV2 in 1 farm
  const targetRewarder = targetFarm?.rewarders?.find(
    (rewarder) => !isNilOrUndef(rewarder.nft) && rewarder?.nft === targetFarm?.ecoFarm,
  )

  // 2. adding config in ecoFarm config
  if (!isNilOrUndef(ecoFarm[targetRewarder?.nft])) {
    return getAddress(targetRewarder.address)
  }

  return null
}

export const getRewarderV2Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<RewarderV2>(rewarderV2Abi, address, signer)
}

export const useRewarderV2Contract = (address: string) => {
  const { library } = useWeb3React<Web3Provider>()

  return getRewarderV2Contract(address || DEFAULT_REWARDER_V2_ADDRESS, library?.getSigner() || simpleRpcProvider)
}

export const useViewEcoBoostFactor = (
  address: string,
  farmId: number,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const rewarderV2Contract = useRewarderV2Contract(address)
  const { account } = useActiveWeb3React()

  return useQuery(
    [QueryKey.REWARDER_V2_GET_BOOST_FACTOR, account, address, farmId],
    async () => {
      const res = await rewarderV2Contract.viewBoostFactor(account, farmId)

      return res
    },
    {
      ...queryOptions,
      enabled: !!rewarderV2Contract && !!address && !!account && !!farmId && (queryOptions?.enabled ?? true),
    },
  )
}

export const useViewPoolInfo = (
  address: string,
  farmId: number,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const rewarderV2Contract = useRewarderV2Contract(address)

  return useQuery(
    [QueryKey.REWARDER_V2_VIEW_POOL_INFO, address, farmId],
    async () => {
      const res = await rewarderV2Contract.poolInfo(farmId)

      return res
    },
    {
      ...queryOptions,
      enabled: !!rewarderV2Contract && !!address && !!farmId && (queryOptions?.enabled ?? true),
    },
  )
}

export const useViewUserInfo = (
  address: string,
  farmId: number,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const rewarderV2Contract = useRewarderV2Contract(address)
  const { account } = useActiveWeb3React()

  return useQuery(
    [QueryKey.REWARDER_V2_VIEW_USER_INFO, account, address, farmId],
    async () => {
      if (!rewarderV2Contract) return null

      const res = await rewarderV2Contract.getUserInfo(farmId, account)

      return res
    },
    {
      ...queryOptions,
      enabled: !!rewarderV2Contract && !!address && !!farmId && !!account && (queryOptions?.enabled ?? true),
    },
  )
}

export const useArgoNftIsApprovalForAll = (
  operatorAddress: string,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const argoNftContract = useArgoNftContract()
  const { account } = useActiveWeb3React()

  return useQuery(
    [QueryKey.REWARDER_V2_ARGO_APPROVAL_IS_FOR_ALL, account, operatorAddress],
    async () => {
      if (!account) return null
      const res = await argoNftContract.isApprovedForAll(account, operatorAddress)
      return res
    },
    {
      ...queryOptions,
      enabled: !!operatorAddress && !!account && (queryOptions?.enabled ?? true),
    },
  )
}

export const useArgoNFTStakingApproval = () => {
  const { toastError } = useToast()
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const contract = useArgoNftContract()
  const addTransaction = useTransactionAdder()
  const { account } = useActiveWeb3React()

  // disable 'unused' rule here to hide unwanted mutate function from consumer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutateAsync, mutate, data, error, ...rest } = useMutation(
    async (operatorAddress: string) => {
      const tx = await contract.setApprovalForAll(operatorAddress, true)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: (successData, operatorAddress) => {
        queryClient.invalidateQueries([QueryKey.REWARDER_V2_ARGO_APPROVAL_IS_FOR_ALL, account, operatorAddress])
      },
    },
  )

  const isApproved = useMemo(() => {
    return !!(data && !error)
  }, [data, error])

  const approve = useCallback(
    async (operatorAddress: string) => {
      try {
        const { tx } = await mutateAsync(operatorAddress)
        addTransaction(tx, {
          title: t('Approve NFT staking Completed'),
          summary: t('Approve NFT staking'),
        })
        return true
      } catch (e) {
        toastError(t('Transaction Failed'), t('Please try again later'))
        return false
      }
    },
    [mutateAsync, t, addTransaction, toastError],
  )

  return {
    isApproved,
    approve,
    ...rest,
  }
}

export const useStakeNfts = (address: string) => {
  const rewarderV2Contract = useRewarderV2Contract(address)
  const queryClient = useQueryClient()
  const { account } = useActiveWeb3React()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await rewarderV2Contract.stakeNfts(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: (data, { farmId }) => {
        queryClient.invalidateQueries([QueryKey.REWARDER_V2_VIEW_USER_INFO, account, address, farmId])
        queryClient.invalidateQueries([QueryKey.REWARDER_V2_GET_BOOST_FACTOR, account, address, farmId])
        queryClient.invalidateQueries(QueryKey.VVS_INFO_API_META_USER_NFT_COLLECTIONS)
      },
    },
  )
}

export const useUnStakeNfts = (address: string) => {
  const rewarderV2Contract = useRewarderV2Contract(address)
  const queryClient = useQueryClient()
  const { account } = useActiveWeb3React()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await rewarderV2Contract.unstakeNfts(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: (data, { farmId }) => {
        queryClient.invalidateQueries([QueryKey.REWARDER_V2_VIEW_USER_INFO, account, address, farmId])
        queryClient.invalidateQueries([QueryKey.REWARDER_V2_GET_BOOST_FACTOR, account, address, farmId])
        queryClient.invalidateQueries(QueryKey.VVS_INFO_API_META_USER_NFT_COLLECTIONS)
      },
    },
  )
}
